import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Pagination, Paper, Stack, TextField, Typography } from '@mui/material';
import DashboardLayout from '../layouts/DashboardLayout';
import api from '../api/client';

const time = value => value ? new Date(value).toLocaleString() : 'Not recorded';

export default function ArchivedCalls() {
  const [search, setSearch] = useState(''), [query, setQuery] = useState(''), [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState({ items: [], total: 0 }), [loading, setLoading] = useState(false), [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null), [detail, setDetail] = useState(null), [detailError, setDetailError] = useState('');
  const [videoId, setVideoId] = useState(null), [playback, setPlayback] = useState(''), [videoError, setVideoError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError('');
    api.get('/incidents/archived', { params: { search: query, page, pageSize: 25 }, signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setResult(response.data); })
      .catch(() => { if (!controller.signal.aborted) setError('Archived calls could not be loaded. Try again.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [query, page, refresh]);

  useEffect(() => {
    setDetail(null); setDetailError(''); setVideoId(null);
    if (selectedId === null) return undefined;
    const controller = new AbortController();
    api.get(`/incidents/archived/${selectedId}`, { signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setDetail(response.data); })
      .catch(() => { if (!controller.signal.aborted) setDetailError('This archived call could not be loaded.'); });
    return () => controller.abort();
  }, [selectedId]);

  useEffect(() => {
    setPlayback(''); setVideoError('');
    if (videoId === null) return undefined;
    const controller = new AbortController();
    api.get(`/videos/presigned-url/${videoId}`, { signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setPlayback(response.data.url); })
      .catch(() => { if (!controller.signal.aborted) setVideoError('Footage is unavailable. The stored association has been retained.'); });
    return () => controller.abort();
  }, [videoId]);

  return <DashboardLayout title="Archived calls">
    <Stack spacing={2}>
      <Button component={Link} to="/admin-panel" sx={{ alignSelf: 'flex-start' }}>Admin panel</Button>
      <Alert severity="info">Archived calls are read-only. Call records, participants and footage associations are retained. CAD references can also be used by active calls; the record ID identifies each archived call.</Alert>
      <Box component="form" onSubmit={event => { event.preventDefault(); setPage(1); setQuery(search.trim()); setRefresh(value => value + 1); }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField fullWidth label="Search CAD reference or incident type" value={search} onChange={event => setSearch(event.target.value)} inputProps={{ maxLength: 256 }} />
          <Button type="submit" variant="contained" disabled={loading}>Search</Button>
        </Stack>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? <CircularProgress aria-label="Loading archived calls" /> : error ? null : <>
        <Typography variant="body2">{result.total} archived calls</Typography>
        {result.items.map(call => <Paper key={call.id} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="h6">{call.cadId} · {call.incidentType}</Typography>
            <Typography variant="body2">Record ID {call.id} · Occurred {time(call.occurredAtUtc)}</Typography>
            <Typography variant="body2" color="text.secondary">Archived {time(call.archivedAtUtc)} by account {call.archivedByUserId ?? 'not recorded'}</Typography>
            <Button variant="outlined" onClick={() => setSelectedId(call.id)} sx={{ alignSelf: 'flex-start' }}>View archived call</Button>
          </Stack>
        </Paper>)}
        {result.total === 0 && <Typography>No archived calls match this search.</Typography>}
        {result.total > 25 && <Pagination page={page} count={Math.ceil(result.total / 25)} onChange={(_, value) => setPage(value)} aria-label="Archived call pages" />}
      </>}
    </Stack>
    <Dialog open={selectedId !== null} onClose={() => setSelectedId(null)} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>{detail ? `Archived ${detail.cadId} · record ${detail.id}` : 'Archived call'}</DialogTitle>
      <DialogContent dividers>
        {detailError ? <Alert severity="error">{detailError}</Alert> : !detail ? <CircularProgress aria-label="Loading call details" /> : <Stack spacing={2}>
          <Chip label="Archived · read-only" sx={{ alignSelf: 'flex-start' }} />
          <Typography>{detail.incidentType} · {time(detail.occurredAtUtc)}{detail.occurredEndUtc ? ` to ${time(detail.occurredEndUtc)}` : ''}</Typography>
          <Typography>{detail.locationDescription || 'Location not recorded'}</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{detail.notes || 'No notes recorded.'}</Typography>
          <Typography variant="subtitle1">Participants</Typography>
          {detail.participants.map(participant => <Typography key={participant.id} variant="body2">{participant.rangerId} · {participant.role} · joined {time(participant.createdAtUtc)}{participant.leftAtUtc ? ` · left ${time(participant.leftAtUtc)}` : ''}</Typography>)}
          <Typography variant="subtitle1">Associated footage</Typography>
          {detail.videos.length === 0 && <Typography variant="body2">No footage is linked to this call.</Typography>}
          {detail.videos.map(video => <Button key={video.id} variant="outlined" onClick={() => setVideoId(video.id)}>{video.cameraType} · {video.rangerId} · {time(video.timestamp)}</Button>)}
          {videoError && <Alert severity="warning">{videoError}</Alert>}
          {videoId !== null && !playback && !videoError && <CircularProgress aria-label="Loading footage" />}
          {playback && <video key={playback} controls playsInline preload="metadata" src={playback} style={{ width: '100%', maxHeight: 420 }} onError={() => setVideoError('This footage could not be played. Its stored association is retained.')} />}
        </Stack>}
      </DialogContent>
      <DialogActions><Button onClick={() => setSelectedId(null)}>Close</Button></DialogActions>
    </Dialog>
  </DashboardLayout>;
}
